import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PredictionSummary = ({ predictions, totalPoints, earlyBonus, onSubmit, onClear, isSubmitting, disabled, isGroupStage, winnerError }) => {
  const allSummaryItems = [
    {
      label: 'Match Winner',
      value: predictions?.winner?.name || 'Not selected',
      points: predictions?.winner ? predictions?.winnerPoints : 0,
      icon: 'Trophy'
    },
    {
      label: 'Toss Winner',
      value: predictions?.tossWinner?.name || 'Not selected',
      points: predictions?.tossWinner ? predictions?.tossPoints : 0,
      icon: 'Coins'
    },
    {
      label: 'Batting First',
      value: predictions?.battingFirst?.name || 'Not selected',
      points: predictions?.battingFirst ? predictions?.battingPoints : 0,
      icon: 'Target'
    },
    {
      label: 'Man of the Match',
      value: predictions?.motm?.name || 'Not selected',
      points: predictions?.motm ? predictions?.motmPoints : 0,
      icon: 'Award'
    },
    {
      label: 'Highest Run Scorer',
      value: predictions?.highestScorer?.name || 'Not selected',
      points: predictions?.highestScorer ? predictions?.scorerPoints : 0,
      icon: 'TrendingUp'
    },
    {
      label: 'Highest Wicket Taker',
      value: predictions?.highestWicketTaker?.name || 'Not selected',
      points: predictions?.highestWicketTaker ? predictions?.wicketPoints : 0,
      icon: 'Zap'
    }
  ];

  // Filter items based on match stage
  const summaryItems = isGroupStage 
    ? allSummaryItems?.filter(item => item?.label === 'Match Winner' || item?.label === 'Man of the Match')
    : allSummaryItems;

  return (
    <div className="bg-card rounded-lg shadow-elevation-md p-4 md:p-6 lg:p-8 sticky top-20 lg:top-24">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="FileText" size={20} className="text-primary" />
        </div>
        <h3 className="text-lg md:text-xl font-semibold text-foreground">Prediction Summary</h3>
      </div>
      <div className="space-y-3 md:space-y-4 mb-4 md:mb-6">
        {summaryItems?.map((item, index) => (
          <div key={index} className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0">
            <div className="flex items-start gap-2 flex-1 min-w-0">
              <Icon name={item?.icon} size={16} className="text-primary mt-1 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-xs md:text-sm font-medium text-foreground">{item?.label}</p>
                <p className="text-xs md:text-sm text-muted-foreground truncate">{item?.value}</p>
              </div>
            </div>
            <span className="text-sm md:text-base font-semibold text-primary whitespace-nowrap">
              {item?.points} pts
            </span>
          </div>
        ))}
      </div>
      <div className="space-y-3 p-4 bg-muted/50 rounded-lg mb-4 md:mb-6">
        <div className="flex items-center justify-between">
          <span className="text-sm md:text-base text-muted-foreground">Base Points:</span>
          <span className="text-base md:text-lg font-semibold text-foreground">{totalPoints} pts</span>
        </div>
        {earlyBonus > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Icon name="Zap" size={16} className="text-warning" />
              <span className="text-sm md:text-base text-muted-foreground">Early Bonus:</span>
            </div>
            <span className="text-base md:text-lg font-semibold text-warning">+{earlyBonus} pts</span>
          </div>
        )}
        <div className="pt-3 border-t border-border flex items-center justify-between">
          <span className="text-base md:text-lg font-semibold text-foreground">Total Potential:</span>
          <span className="text-xl md:text-2xl font-bold text-primary">{totalPoints + earlyBonus} pts</span>
        </div>
      </div>
      {earlyBonus > 0 && (
        <div className="flex items-start gap-2 p-3 bg-success/10 rounded-lg mb-4 md:mb-6">
          <Icon name="Gift" size={16} className="text-success mt-0.5 flex-shrink-0" />
          <p className="text-xs md:text-sm text-success">
            You're eligible for early prediction bonus! Submit now to secure extra points.
          </p>
        </div>
      )}
      <div className="space-y-2 md:space-y-3">
        <Button
          variant="default"
          fullWidth
          onClick={onSubmit}
          loading={isSubmitting}
          disabled={disabled}
          iconName="Send"
          iconPosition="right"
        >
          Submit Predictions
        </Button>
        {winnerError && (
          <div id="match-prediction-error-winner" className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>{winnerError}</span>
          </div>
        )}
        <Button
          variant="ghost"
          fullWidth
          onClick={onClear}
          disabled={disabled || isSubmitting}
          iconName="X"
          iconPosition="left"
        >
          Clear All
        </Button>
      </div>
      <div className="mt-4 md:mt-6 pt-4 md:pt-6 border-t border-border">
        <div className="flex items-start gap-2">
          <Icon name="Info" size={16} className="text-muted-foreground mt-0.5 flex-shrink-0" />
          <p className="text-xs text-muted-foreground">
            Predictions can be modified until 1 hour before match start. Points are calculated automatically after match results are posted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PredictionSummary;